import { ThunkDispatch } from "core-integration/src/store";
import { getCurrentEventStatus } from "core-integration/src/store/events/reducers";
import { fetchEventStatus } from "core-integration/src/store/events/thunks";
import { formatRawAsLocal } from "core-integration/src/utils/datetime";
import { Cell, Column, Row, Table } from "plos/src/components/Table";
import { InfoText } from "plos/src/components/tooltips/InfoText";
import { fixedTable } from "plos/src/styles/utils.css";
import { useEffect } from "react";
import { TableBody, TableHeader } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  dateCell,
  dateCol,
  pointsStatusCell,
  pointsStatusCol,
  pointsStatusIndicator,
  pointsStatusIndicatorProvisional,
  pointsStatusIndicatorStarted,
} from "./status.css";
import { ReadableStatus } from "./types";
import { getReadableStatus } from "./utils";

const EventStatus = () => {
  const dispatch = useDispatch<ThunkDispatch>();
  const statusData = useSelector(getCurrentEventStatus);

  const { t } = useTranslation("status");

  useEffect(() => {
    dispatch(fetchEventStatus());
  }, [dispatch]);

  const statusLabels: Record<ReadableStatus, string> = {
    notStarted: t("notStarted", "Not started"),
    live: t("live", "Live"),
    provisional: t("provisional", "Provisional"),
    confirmed: t("confirmed", "Confirmed"),
  };

  const getStatusClassName = (status: ReadableStatus) => {
    if (status === "provisional") {
      return pointsStatusIndicatorProvisional;
    }
    if (status !== "notStarted") {
      return pointsStatusIndicatorStarted;
    }
    return "";
  };

  const dateFormat = "EEEE d MMM";

  if (!statusData) {
    return null;
  }

  return (
    <div>
      <Table
        className={fixedTable}
        aria-label={t("eventStatus", "Event Status")!}
      >
        <TableHeader>
          <Column isRowHeader className={dateCol}>
            {t("day", "Day")}
          </Column>
          <Column className={pointsStatusCol}>
            <InfoText
              label={t("status", "Status")}
              tooltipText={t(
                "statusTooltipText",
                "Points and bonus update live during matches. A day becomes Provisional once every match finishes, then Confirmed after official data is checked (including assist confirmations).",
              )!}
            >
              {t("status", "Status")}
            </InfoText>
          </Column>
        </TableHeader>
        <TableBody>
          {statusData.status.map((s) => {
            const status = getReadableStatus(s);

            return (
              <Row key={s.date}>
                <Cell className={dateCell}>
                  {formatRawAsLocal(s.date, dateFormat)}
                </Cell>
                <Cell className={pointsStatusCell}>
                  <div
                    className={`${pointsStatusIndicator} ${getStatusClassName(
                      status,
                    )}`}
                  >
                    {statusLabels[status]}
                  </div>
                </Cell>
              </Row>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default EventStatus;
